/**
 * Sharetribe session token helpers shared by the browser SDK store and
 * the server privileged-API token store.
 *
 * Cookie name matches sharetribe-flex-sdk: `st-${clientId}-token`.
 * Never log access_token or refresh_token.
 */

export const SHARETRIBE_TOKEN_COOKIE_NS = 'st';
export const SHARETRIBE_TOKEN_HEADER = 'x-sharetribe-token';
export const SESSION_EXPIRED_CODE = 'session_expired';
export const SESSION_EXPIRED_MESSAGE = 'Please sign in again to complete payment.';
export const TRUSTED_API_CODE = 'trusted_api_unavailable';
export const TRUSTED_API_MESSAGE =
  'You are still signed in. Payment cannot start because the server cannot open a trusted checkout session. This is not a login problem.';

const TOKEN_FIELDS = ['access_token', 'refresh_token', 'token_type', 'scope', 'expires_in'];
const USER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Webpack can load this file in more than one chunk. A module-level
// `let` then splits the signed-in token from /api/presence calls and
// Sharetribe answers 403 (private marketplace + public-read cookie).
const tokenRoot = typeof globalThis === 'object' && globalThis ? globalThis : global;
if (!tokenRoot.__sweetListenersSharetribeBrowserToken) {
  tokenRoot.__sweetListenersSharetribeBrowserToken = { current: null };
}
const browserTokenSlot = tokenRoot.__sweetListenersSharetribeBrowserToken;

const getModuleMemoryToken = () => browserTokenSlot.current;
const setModuleMemoryToken = next => {
  browserTokenSlot.current = next;
};

/**
 * Cookie name used by the Sharetribe JS SDK cookie token stores.
 *
 * @param {string} clientId
 * @returns {string}
 */
export const sharetribeTokenCookieName = clientId =>
  `${SHARETRIBE_TOKEN_COOKIE_NS}-${clientId}-token`;

/**
 * True when this token can be exchanged for a trusted checkout token.
 * Anonymous client-credentials tokens (`public-read`) 401 on token_exchange.
 *
 * @param {Object|null|undefined} token
 * @returns {boolean}
 */
export const isUserAccessToken = token => {
  if (!token || typeof token !== 'object') {
    return false;
  }
  if (typeof token.access_token !== 'string' || token.access_token.length < 8) {
    return false;
  }
  const scope = String(token.scope || '');
  if (scope === 'public-read' || scope === 'public' || scope.split(' ').includes('public-read')) {
    return false;
  }
  if (scope) {
    return true;
  }
  // Sharetribe AuthInfo: tokens without scope are user tokens iff they have a refresh token.
  if (typeof token.refresh_token === 'string' && token.refresh_token.length > 8) {
    return true;
  }
  // Compact header may send only the access token; JWT user-id is enough.
  return !!userIdFromAccessToken(token);
};

/**
 * @param {Object|null|undefined} token
 * @returns {Object|null}
 */
export const pickTokenFields = token => {
  if (!token || typeof token !== 'object') {
    return null;
  }
  const picked = TOKEN_FIELDS.reduce((acc, key) => {
    if (token[key] !== undefined && token[key] !== null) {
      acc[key] = token[key];
    }
    return acc;
  }, {});
  if (token.isLoggedInAs) {
    picked.isLoggedInAs = true;
  }
  return picked.access_token ? picked : null;
};

/**
 * Parse a cookie-parser or document.cookie token value.
 *
 * @param {string|Object|null|undefined} raw
 * @returns {Object|null}
 */
export const parseTokenCookieValue = raw => {
  if (!raw) {
    return null;
  }
  if (typeof raw === 'object') {
    return pickTokenFields(raw);
  }
  if (typeof raw !== 'string') {
    return null;
  }
  try {
    return pickTokenFields(JSON.parse(raw));
  } catch (e) {
    return null;
  }
};

const utf8ToBase64 = json => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(json, 'utf8').toString('base64');
  }
  return btoa(unescape(encodeURIComponent(json)));
};

const base64ToUtf8 = value => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'base64').toString('utf8');
  }
  return decodeURIComponent(escape(atob(value)));
};

/**
 * Compact header payload for same-origin privileged API calls.
 * Skip if the token is missing or too large for a header.
 *
 * @param {Object|null|undefined} token
 * @returns {string|null}
 */
const encodeTokenPayload = payload => {
  const json = JSON.stringify(payload);
  if (json.length > 3500) {
    return null;
  }
  return utf8ToBase64(json);
};

export const encodeTokenHeaderValue = token => {
  const picked = pickTokenFields(token);
  if (!isUserAccessToken(picked)) {
    return null;
  }
  const full = encodeTokenPayload(picked);
  if (full) {
    return full;
  }
  // Refresh tokens blow past typical header/cookie limits. Access token +
  // scope is enough for /api/presence and JWT user-id fallback.
  const compact = {
    access_token: picked.access_token,
    token_type: picked.token_type || 'bearer',
  };
  if (picked.isLoggedInAs) {
    compact.isLoggedInAs = true;
  }
  if (picked.scope) {
    compact.scope = picked.scope;
  } else if (userIdFromAccessToken(picked)) {
    compact.scope = 'user';
  } else if (picked.refresh_token) {
    compact.refresh_token = picked.refresh_token;
  }
  return encodeTokenPayload(compact);
};

/**
 * @param {string|null|undefined} headerValue
 * @returns {Object|null}
 */
export const decodeTokenHeaderValue = headerValue => {
  if (!headerValue || typeof headerValue !== 'string') {
    return null;
  }
  const trimmed = headerValue.trim();
  if (!trimmed || trimmed.length > 8000) {
    return null;
  }
  try {
    const parsed = parseTokenCookieValue(base64ToUtf8(trimmed));
    return isUserAccessToken(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

/**
 * JWT `exp` (ms) of a Marketplace access token, or 0. Never logs the token.
 */
export const accessTokenExpiresAt = token => {
  const access = typeof token === 'string' ? token : token?.access_token;
  if (!access || typeof access !== 'string' || access.split('.').length < 2) {
    return 0;
  }
  try {
    const part = access.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    const exp = Number(JSON.parse(base64ToUtf8(padded))?.exp);
    return Number.isFinite(exp) ? exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Refresh-storm fix (Oct 10): of two tokens for the same user, the one whose
 * access token expires later is the fresher one (a server refresh wrote it to
 * the cookie). Returns 'a', 'b' or null when equal/unknown.
 */
const fresherOf = (a, b) => {
  const ea = accessTokenExpiresAt(a);
  const eb = accessTokenExpiresAt(b);
  if (ea && eb && ea !== eb) {
    return ea > eb ? 'a' : 'b';
  }
  return null;
};

/**
 * Prefer the live browser token (header) over a stale/anonymous cookie.
 * If header and cookie are both user tokens for *different* people, the
 * cookie wins — Login-as and a fresh sign-in write the cookie, while a
 * stale tab can still send the previous user's header.
 *
 * @param {Object|null|undefined} cookieToken
 * @param {Object|null|undefined} headerToken
 * @returns {Object|null}
 */
export const resolveRequestToken = (cookieToken, headerToken) => {
  const headerUser = isUserAccessToken(headerToken);
  const cookieUser = isUserAccessToken(cookieToken);
  if (headerUser && cookieUser) {
    const headerId = userIdFromAccessToken(headerToken);
    const cookieId = userIdFromAccessToken(cookieToken);
    if (headerId && cookieId && headerId !== cookieId) {
      return pickTokenFields(cookieToken);
    }
    // Same user: an expired header token must not trigger a server refresh
    // when the cookie already carries a newer one.
    if (fresherOf(cookieToken, headerToken) === 'a') {
      const merged = pickTokenFields(cookieToken);
      if (!merged.refresh_token && headerToken.refresh_token) {
        merged.refresh_token = headerToken.refresh_token;
      }
      return merged;
    }
    return pickTokenFields(headerToken);
  }
  if (headerUser) {
    return pickTokenFields(headerToken);
  }
  if (cookieUser) {
    return pickTokenFields(cookieToken);
  }
  return null;
};

/**
 * Read `user-id` from a Marketplace access token. Never logs the token.
 *
 * @param {Object|string|null|undefined} token
 * @returns {string|null}
 */
export const userIdFromAccessToken = token => {
  const access = typeof token === 'string' ? token : token?.access_token;
  if (!access || typeof access !== 'string' || access.split('.').length < 2) {
    return null;
  }
  try {
    const part = access
      .split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    const payload = JSON.parse(base64ToUtf8(padded));
    const id = payload?.['user-id'] || payload?.user_id || payload?.sub;
    return typeof id === 'string' && USER_ID_RE.test(id) ? id : null;
  } catch (e) {
    return null;
  }
};

/**
 * Error forwarded by handleError as JSON so checkout can show sign-in copy
 * instead of a raw Auth API "Unauthorized".
 *
 * @param {string} [message]
 * @returns {Error}
 */
export const sessionExpiredError = (message = SESSION_EXPIRED_MESSAGE) => {
  const error = new Error(message);
  error.status = 401;
  error.statusText = message;
  error.data = { code: SESSION_EXPIRED_CODE, message };
  error.step = 'session';
  return error;
};

/**
 * User token was present but token_exchange was rejected (bad Application
 * secret, or Sharetribe Auth API 401). Do not tell the user to sign in again.
 *
 * @param {string} [message]
 * @returns {Error}
 */
export const trustedApiUnavailableError = (message = TRUSTED_API_MESSAGE) => {
  const error = new Error(message);
  error.status = 503;
  error.statusText = message;
  error.data = { code: TRUSTED_API_CODE, message };
  error.step = 'exchangeToken';
  return error;
};

const readDocumentCookie = (name, cookieString) => {
  const source = cookieString || (typeof document !== 'undefined' ? document.cookie : '');
  if (!source || !name) {
    return null;
  }
  const parts = source.split('; ');
  for (let i = 0; i < parts.length; i++) {
    const eq = parts[i].indexOf('=');
    if (eq < 0) {
      continue;
    }
    const key = decodeURIComponent(parts[i].slice(0, eq));
    if (key === name) {
      try {
        return decodeURIComponent(parts[i].slice(eq + 1));
      } catch (e) {
        return parts[i].slice(eq + 1);
      }
    }
  }
  return null;
};

/**
 * Read the Sharetribe token the browser SDK last wrote (or in-memory copy).
 *
 * @param {{ clientId?: string, memoryToken?: Object, cookieString?: string }} [opts]
 * @returns {Object|null}
 */
export const readBrowserSharetribeToken = (opts = {}) => {
  if (isUserAccessToken(opts.memoryToken)) {
    return pickTokenFields(opts.memoryToken);
  }
  const memory = getModuleMemoryToken();
  const clientId =
    opts.clientId ||
    (typeof process !== 'undefined' ? process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID : '');
  const raw = clientId
    ? readDocumentCookie(sharetribeTokenCookieName(clientId), opts.cookieString)
    : null;
  const fromCookie = parseTokenCookieValue(raw);
  if (isUserAccessToken(memory)) {
    if (
      isUserAccessToken(fromCookie) &&
      fromCookie.refresh_token &&
      userIdFromAccessToken(fromCookie) === userIdFromAccessToken(memory) &&
      fresherOf(fromCookie, memory) === 'a'
    ) {
      setModuleMemoryToken(fromCookie);
      return pickTokenFields(fromCookie);
    }
    return pickTokenFields(memory);
  }
  return isUserAccessToken(fromCookie) ? fromCookie : null;
};

/**
 * Browser token store: memory first (Safari may drop large/JS-set cookies),
 * then a first-party cookie with Path=/ and SameSite=Lax.
 *
 * @param {{ clientId: string, secure?: boolean }} opts
 * @returns {{ getToken: Function, setToken: Function, removeToken: Function }}
 */
export const createBridgedBrowserTokenStore = ({ clientId, secure = false }) => {
  const key = sharetribeTokenCookieName(clientId);
  const maxAgeSeconds = 60 * 60 * 24 * 30;

  const writeCookie = token => {
    if (typeof document === 'undefined') {
      return;
    }
    const attrs = [`Path=/`, `Max-Age=${maxAgeSeconds}`, `SameSite=Lax`];
    if (secure) {
      attrs.push('Secure');
    }
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(
      JSON.stringify(pickTokenFields(token) || {})
    )}; ${attrs.join('; ')}`;
  };

  const clearCookie = () => {
    if (typeof document === 'undefined') {
      return;
    }
    const attrs = [`Path=/`, `Max-Age=0`, `SameSite=Lax`];
    if (secure) {
      attrs.push('Secure');
    }
    document.cookie = `${encodeURIComponent(key)}=; ${attrs.join('; ')}`;
  };

  // Server-set cookie (Sam, Oct 10): Safari caps cookies written by JS at
  // 7 days. After every JS write we ask the server to re-send the same
  // cookie with Set-Cookie (30 days), which Safari does not cap.
  let persistTimer = null;
  let persistedOnLoad = false;
  const canPersist = () =>
    typeof window !== 'undefined' && typeof fetch === 'function' && !!window.location;
  const persistServerSide = token => {
    if (!canPersist() || !isUserAccessToken(token)) {
      return;
    }
    if (persistTimer) {
      clearTimeout(persistTimer);
    }
    const body = JSON.stringify(pickTokenFields(token));
    persistTimer = setTimeout(() => {
      persistTimer = null;
      try {
        fetch('/api/auth/persist-token', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'text/plain' },
          body,
        }).catch(() => {});
      } catch (e) {
        // ignore — JS cookie still works
      }
    }, 300);
  };
  const clearServerSide = () => {
    if (persistTimer) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    if (!canPersist()) {
      return;
    }
    try {
      fetch('/api/auth/clear-token', { method: 'POST', credentials: 'same-origin' }).catch(
        () => {}
      );
    } catch (e) {
      // ignore
    }
  };

  return {
    getToken: () => {
      const memory = getModuleMemoryToken();
      const fromCookie = parseTokenCookieValue(readDocumentCookie(key));
      if (isUserAccessToken(memory) && isUserAccessToken(fromCookie)) {
        const memoryId = userIdFromAccessToken(memory);
        const cookieId = userIdFromAccessToken(fromCookie);
        if (memoryId && cookieId && memoryId !== cookieId) {
          setModuleMemoryToken(fromCookie);
          return fromCookie;
        }
        // Adopt a token the server refreshed into the cookie.
        if (fresherOf(fromCookie, memory) === 'a' && fromCookie.refresh_token) {
          setModuleMemoryToken(fromCookie);
          return fromCookie;
        }
        return memory;
      }
      if (isUserAccessToken(memory)) {
        return memory;
      }
      if (fromCookie) {
        setModuleMemoryToken(fromCookie);
        if (!persistedOnLoad && isUserAccessToken(fromCookie)) {
          // Upgrade an existing JS-set cookie to a server-set one once per load.
          persistedOnLoad = true;
          persistServerSide(fromCookie);
        }
        return fromCookie;
      }
      return memory || null;
    },
    setToken: token => {
      const next = pickTokenFields(token) || token;
      // Never let an anonymous client_credentials token wipe a signed-in session.
      if (!isUserAccessToken(next) && isUserAccessToken(getModuleMemoryToken())) {
        return;
      }
      setModuleMemoryToken(next);
      try {
        writeCookie(next);
      } catch (e) {
        // Quota / ITP — privileged API calls still send the memory token header.
      }
      persistServerSide(next);
    },
    removeToken: () => {
      setModuleMemoryToken(null);
      try {
        clearCookie();
      } catch (e) {
        // ignore
      }
      clearServerSide();
    },
  };
};

/**
 * Reset the in-memory token (tests only).
 */
export const resetBrowserTokenMemory = () => {
  setModuleMemoryToken(null);
};
